import { Search, X } from "lucide-react";
import { cn } from "../../lib/cn";

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
}

/**
 * The one search field. Matches Input's height, radius, border and focus ring, with a leading
 * magnifier and a clear button that only appears once there is something to clear.
 *
 * The label is visually hidden rather than absent: the icon alone is not an accessible name, and
 * a visible "Search" label above every table would add a row of chrome to each page for no gain.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = "Search…",
  label = "Search",
  className,
}: SearchInputProps) {
  return (
    <div className={cn("relative", className)}>
      <Search
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-muted"
      />
      <input
        type="search"
        aria-label={label}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-10 w-full rounded-control border border-border bg-surface pl-9 pr-9 text-sm text-text",
          "placeholder:text-text-muted",
          "transition-colors duration-fast ease-standard",
          "outline-none focus:border-accent focus:ring-2 focus:ring-accent/20",
          // Safari paints its own clear affordance on type="search"; ours is themed and
          // keyboard-reachable, so the native one is suppressed to avoid two of them.
          "[&::-webkit-search-cancel-button]:appearance-none",
        )}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          className={cn(
            "absolute right-2 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center",
            "rounded-control text-text-muted",
            "transition-colors duration-fast ease-standard hover:bg-surface-hover hover:text-text",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          )}
        >
          <X className="size-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}
